/** Existing data in an additive companion; never a replacement Z total. */
export function AgencyReconciliationView({
  report,
}: {
  report: Record<string, unknown>;
}) {
  const payment = report.payment as Record<string, unknown>;
  const recorded = report.recorded as Record<string, unknown>;
  const money = (n: unknown) =>
    typeof n === "number" ? `£${(n / 100).toFixed(2)}` : "Unverified";
  const labels: Record<string, string> = {
    protectedBaseSubtotalPence: "Shop base subtotal (recorded)",
    protectedItemEntitlementSnapshotPence:
      "Recorded item entitlement (where complete)",
    stripeProcessingCostSnapshotPence:
      "Stripe cost snapshot — not independently verified",
    itemUpliftPence: "Recorded item uplift",
    customerPlatformFeePence: "Customer platform service fee",
    driverRequestFeePence: "Separate driver request fee",
    deliveryPence: "Delivery charge recorded",
    shopServicePence: "Shop service charge",
    bagPence: "Bag charge",
    shopDiscountPence: "Shop-funded discount",
    hubPromoPence: "Hub-funded promotion",
    kindnessCreditPence: "Kindness credit",
    kindnessContributionPence: "Kindness donation",
    refundedPence: "Order refund snapshot",
    shopProceedsSnapshotPence: "Shop proceeds snapshot — not bank receipt",
  };
  return (
    <div className="mt-5 space-y-4">
      <div className="rounded-xl bg-slate-50 p-4">
        <h3 className="font-bold">
          Order #{String(report.orderId)} · {String(report.storeName)}
        </h3>
        <p className="mt-1 text-sm">{String(report.legalStatus)}</p>
        <p className="mt-2 text-sm text-slate-600">
          {String(report.reconciliationStatus)}
        </p>
      </div>
      <div className="rounded-xl border bg-white p-4">
        <h4 className="font-semibold">
          Food value and contractual classification
        </h4>
        <dl className="mt-3 space-y-2 text-sm">
          {[
            [
              "Recorded customer item total before promotions",
              (report.customerSnapshot as { itemsPence?: number })?.itemsPence,
            ],
            [
              "Shop gross food sale — approved ordinary Model A only",
              report.legalShopGrossFoodPence,
            ],
            [
              "Agency commission — approved ordinary Model A only",
              report.agencyCommissionPence,
            ],
            [
              "Driver completed assignment earnings — not bank payout",
              (report.driver as { earnedForCompletedAssignmentsPence?: number })
                ?.earnedForCompletedAssignmentsPence,
            ],
          ].map(([label, value]) => (
            <div className="flex justify-between gap-4" key={String(label)}>
              <dt>{String(label)}</dt>
              <dd className="shrink-0 font-semibold">{money(value)}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-xs text-slate-500">
          {String(report.commissionStatus)}. A Hub-managed delivery charge may
          be Hub’s own gross delivery consideration even where the driver
          receives the same amount.
        </p>
      </div>
      <dl className="divide-y">
        {Object.entries(recorded).map(([k, v]) => (
          <div
            key={k}
            className="flex items-start justify-between gap-4 py-2 text-sm"
          >
            <dt>{labels[k] ?? k}</dt>
            <dd className="shrink-0 font-semibold">{money(v)}</dd>
          </div>
        ))}
      </dl>
      <div className="rounded-xl border p-4">
        <h4 className="font-semibold">Payment receipt evidence</h4>
        <p className="mt-1 text-sm">{String(payment.evidence)}</p>
        <dl className="mt-3 space-y-2 text-sm">
          {[
            ["Captured customer payment", "capturedPence"],
            ["Succeeded refunds", "refundedPence"],
            ["Pending refunds", "pendingRefundPence"],
            ["Captured less succeeded refunds", "netCapturedPence"],
          ].map(([label, key]) => (
            <div className="flex justify-between gap-3" key={key}>
              <dt>{label}</dt>
              <dd className="font-semibold">{money(payment[key])}</dd>
            </div>
          ))}
        </dl>
      </div>
      <p className="text-sm">
        <strong>Delivery supplier:</strong> {String(report.deliverySupplier)}
      </p>
      <details className="rounded-xl border p-4">
        <summary className="cursor-pointer text-sm font-semibold">
          Driver assignment and posted / void ledger evidence
        </summary>
        <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap break-all text-xs">
          {JSON.stringify(
            { driver: report.driver, ledger: report.ledger },
            null,
            2,
          )}
        </pre>
      </details>
      <p className="rounded-xl bg-amber-50 p-4 text-xs leading-5 text-amber-900">
        {String(report.notice)}
      </p>
      <button
        className="rounded-lg border px-4 py-2 text-sm font-semibold"
        onClick={async () => {
          const token = localStorage.getItem("ayrshire_token");
          const r = await fetch(
            `/api/admin/agency/reconciliation/${report.orderId}?format=csv`,
            { headers: { Authorization: `Bearer ${token ?? ""}` } },
          );
          if (!r.ok) return;
          const url = URL.createObjectURL(await r.blob());
          const a = document.createElement("a");
          a.href = url;
          a.download = `agency-companion-order-${report.orderId}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        }}
      >
        Download accountant evidence CSV
      </button>
    </div>
  );
}
